// @file Composable that provides methods for droppable sections
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import type { OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import type { Post, WallGroupPost } from '@@/types'
import { useSurfacePostSectionDraggableHelper } from '@@/vuecomposables/surface_post_section_draggable_helper'
import { storeToRefs } from 'pinia'
import { computed, ref, type Ref } from 'vue'

export const SURFACE_SECTION_MANUAL_DRAG_PREVIEW_ID = 'surface-section-manual-drag-preview'

export const useSurfaceSectionDroppableElement = (params: {
  sectionElementRef: Ref<HTMLElement | undefined>
  group: WallGroupPost
  type: string
  axis: 'horizontal' | 'vertical'
  canDrop: Ref<boolean>
}): {
  isDraggingPostIntoEmptySection: typeof isDraggingPostIntoEmptySection
} => {
  const { sectionElementRef, group, type, axis, canDrop } = params
  const { groupIdUnderPostBeingDragged, isDraggingPostInNonManuallySortedWall } = storeToRefs(
    useSurfacePostsDragAndDropStore(),
  )
  const { allSortedPostsByGroup } = storeToRefs(useSurfacePostsStore())

  const isDraggingPostIntoEmptySection = ref(false)
  const { addDraggableSource: addDraggableSourceForSection } = useDropTargetElement({
    elementRef: sectionElementRef,
    type,
    data: computed(() => {
      return { section: group }
    }),
  })

  const { canDropPostIntoSection, dropPostIntoSection } = useSurfacePostSectionDraggableHelper()
  addDraggableSourceForSection({
    type,
    axis,
    onDrop: (e: OnDropPayload<{ post: Post }, { section: WallGroupPost }>) => {
      isDraggingPostIntoEmptySection.value = false
      dropPostIntoSection(e)
    },
    onDragEnter: () => {
      groupIdUnderPostBeingDragged.value = group.id
      // If the wall is non-manually sorted, we don't show the drop indicator line
      if (isDraggingPostInNonManuallySortedWall.value) return
      const posts = allSortedPostsByGroup.value[group.id]
      isDraggingPostIntoEmptySection.value = posts?.length === 0
    },
    onDragLeave: () => {
      isDraggingPostIntoEmptySection.value = false
    },
    canDrop: () => {
      return canDrop.value && canDropPostIntoSection(group.id)
    },
  })

  return {
    isDraggingPostIntoEmptySection,
  }
}
